import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Breadcrumb, Icon, Popconfirm, message } from 'antd';
import { Base64 } from 'js-base64';

import { QUERY_PMS_DETAIL, MUTATION_UPDATE_PMS } from '../quires';
import { pmsDetailTabs, pmsStatus, pmsStatusEnum } from '../constants';

import DetailLayout from './menu';
import BasicInfo from './components/basic-info';
import Contact from './components/contact';
import LandlordList from './components/landlord-list';

export default function PMSDetails() {
  const { t } = useTranslation(['landlord']);
  const { pathname } = useLocation();
  const pmsId = Base64.decode(pathname.split('/')[3]);

  const [pmsDetail, setPmsDetail] = useState({});
  const [activeTab, setActiveTab] = useState(pmsDetailTabs.BASIC_INFO);

  const handleSetActiveTab = tab => {
    setActiveTab(tab.key);
  };

  const [queryPMSList, { loading }] = useLazyQuery(QUERY_PMS_DETAIL, {
    fetchPolicy: 'no-cache',
    onCompleted: res => {
      const response = res?.getPMS;
      setPmsDetail(response);
    },
  });

  const [updatePMSAccount] = useMutation(MUTATION_UPDATE_PMS, {
    onCompleted: result => {
      if (result?.updatePMS?.pms?.id) {
        queryPMS();
      }
    },
    onError: e => {
      if (e) {
        message.error(e.toString());
      } else {
        message.error(t('Something wrong, please try again.'));
      }
    },
  });

  const queryPMS = () => queryPMSList({ variables: { id: pmsId } });

  const handleOnConfirm = () => {
    updatePMSAccount({
      variables: {
        input: {
          id: pmsDetail.id,
          status:
            pmsDetail.status === pmsStatusEnum.AT_WORK
              ? pmsStatusEnum.TERMINATED
              : pmsStatusEnum.PENDING,
        },
      },
    });
  };

  useEffect(() => {
    queryPMS();
  }, []);

  return (
    <div className="pms-detail">
      <div className="detail-header">
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to="/pms-integrations">{t('cms:cms.sidebar.menu.pms_integrations')}</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{t('cms:cms.pms_integrations.detail.pms_details')}</Breadcrumb.Item>
        </Breadcrumb>
        <div className="status-row">
          <span className="status-name">{pmsDetail?.name}</span>
          <div className="status-wrapper">
            <span className="current-status">
              {t('cms:cms.pms_integrations.detail.header.current_status.label')}
            </span>
            <span className={`status-label ${pmsDetail.status}`}>
              {pmsDetail.status === pmsStatusEnum.TERMINATED
                ? 'Terminate'
                : pmsStatus[pmsDetail.status]}
            </span>
            {[pmsStatusEnum.AT_WORK, pmsStatusEnum.TERMINATED].includes(pmsDetail.status) && (
              <div className="status-action">
                {pmsStatusEnum.AT_WORK === pmsDetail.status && (
                  <Popconfirm
                    overlayStyle={{ maxWidth: 236 }}
                    placement="bottomLeft"
                    title={t('cms:cms.pms_integrations.detail.header.terminate.label')}
                    onConfirm={handleOnConfirm}
                    okText={t('cms:cms.tag_modal.header.btn.confirm')}
                    cancelText={t('cms:cms.tag_modal.header.btn.cancel')}
                    okType="danger"
                  >
                    <Icon type="stop" className="status-icon" />
                    <span>Terminate</span>
                  </Popconfirm>
                )}
                {pmsStatusEnum.TERMINATED === pmsDetail.status && (
                  <Popconfirm
                    overlayStyle={{ maxWidth: 236 }}
                    placement="bottomLeft"
                    title={t('cms:cms.pms_integrations.detail.header.reopen.label')}
                    onConfirm={handleOnConfirm}
                    okText={t('cms:cms.tag_modal.header.btn.confirm')}
                    cancelText={t('cms:cms.tag_modal.header.btn.cancel')}
                  >
                    <Icon type="api" className="reopen-icon" />
                    <span style={{ color: '#38b2a6' }}>
                      {t('cms:cms.pms_integrations.detail.header.status.reopen.label')}
                    </span>
                  </Popconfirm>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <DetailLayout activeTab={activeTab} handleSetActiveTab={handleSetActiveTab}>
        {activeTab === pmsDetailTabs.BASIC_INFO && (
          <BasicInfo t={t} pmsDetail={pmsDetail} refetch={queryPMS} />
        )}
        {activeTab === pmsDetailTabs.CONTACT && (
          <Contact t={t} contactInfo={pmsDetail?.contact ?? {}} refetch={queryPMS} pmsId={pmsId} />
        )}
        {activeTab === pmsDetailTabs.INTEGRATED_LANDLORD_LIST && (
          <LandlordList
            t={t}
            pmsDetail
            landlordList={pmsDetail?.landlords ?? {}}
            queryLandlordByParams={queryPMSList}
            pmsId={pmsId}
            loading={loading}
          />
        )}
      </DetailLayout>
    </div>
  );
}
